import { type FormEvent, useState } from 'react'
import { UserPlus, Users, Loader2, Check, ChevronsUpDown } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'
import { Separator } from '@/components/ui/separator'
import type { MetodoPagamento, StatusInscricao } from '@/pages/evento-detalhe/types'
import type { ModoInclusao, ParticipanteFormState } from '../types'

interface ParticipantFormCardProps {
  modo: ModoInclusao
  onModoChange: (modo: ModoInclusao) => void
  membros: Array<{ id: string; nome_exibicao: string; ja_participando?: boolean }>
  membroSelecionadoId: string
  onSelecionarMembro: (id: string) => void
  form: ParticipanteFormState
  onFieldChange: <K extends keyof ParticipanteFormState>(
    field: K,
    value: ParticipanteFormState[K]
  ) => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  onCancel: () => void
  isSubmitting: boolean
  eventoGratuito?: boolean
}

export function ParticipantFormCard({
  modo,
  onModoChange,
  membros,
  membroSelecionadoId,
  onSelecionarMembro,
  form,
  onFieldChange,
  onSubmit,
  onCancel,
  isSubmitting,
  eventoGratuito = false,
}: ParticipantFormCardProps) {
  const [open, setOpen] = useState(false)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserPlus className="h-5 w-5 text-primary" />
          Dados do participante
        </CardTitle>
        <CardDescription>
          Escolha um membro existente ou preencha as informações manualmente
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant={modo === 'existente' ? 'default' : 'outline'}
            onClick={() => onModoChange('existente')}
            size="sm"
          >
            <Users className="mr-2 h-4 w-4" />
            Buscar membro
          </Button>
          <Button
            type="button"
            variant={modo === 'novo' ? 'default' : 'outline'}
            onClick={() => onModoChange('novo')}
            size="sm"
          >
            <UserPlus className="mr-2 h-4 w-4" />
            Cadastro manual
          </Button>
        </div>

        {modo === 'existente' && (
          <div className="space-y-2">
            <Label>Membro da igreja</Label>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between"
                >
                  {membroSelecionadoId
                    ? membros.find((membro) => membro.id === membroSelecionadoId)?.nome_exibicao
                    : "Buscar membro..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <Command>
                  <CommandInput placeholder="Digite o nome do membro..." />
                  <CommandList>
                    <CommandEmpty>Nenhum membro encontrado.</CommandEmpty>
                    <CommandGroup>
                      {membros.map((membro) => (
                        <CommandItem
                          key={membro.id}
                          value={membro.nome_exibicao}
                          onSelect={() => {
                            if (!membro.ja_participando) {
                              onSelecionarMembro(membro.id === membroSelecionadoId ? '' : membro.id)
                              setOpen(false)
                            }
                          }}
                          disabled={membro.ja_participando}
                          className={cn(
                            membro.ja_participando && "opacity-50 cursor-not-allowed"
                          )}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              membro.ja_participando
                                ? "text-emerald-500"
                                : membroSelecionadoId === membro.id
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                          <div className="flex flex-col flex-1">
                            <span>{membro.nome_exibicao}</span>
                            {membro.ja_participando && (
                              <span className="text-xs text-muted-foreground">
                                Já é participante deste evento
                              </span>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {!membros.length && (
              <p className="text-xs text-muted-foreground">
                Nenhum membro encontrado. Utilize o cadastro manual.
              </p>
            )}
          </div>
        )}

        <form className="space-y-5" onSubmit={onSubmit}>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome completo</Label>
              <Input
                id="nome"
                placeholder="Ex: Ana Souza"
                value={form.nome}
                onChange={(event) => onFieldChange('nome', event.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="funcao">Função no evento</Label>
              <Select
                value={
                  form.funcao === 'Visitante' ? 'visitante' :
                  form.funcao === 'Voluntário' ? 'voluntario' :
                  form.funcao === 'Equipe' ? 'equipe' :
                  form.funcao === 'Ministrante' ? 'ministrante' :
                  form.funcao === 'Músico' ? 'musico' :
                  form.funcao === 'Líder' ? 'lider' :
                  form.funcao === '' ? '' : 'outros'
                }
                onValueChange={(value) => {
                  if (value === 'outros') {
                    onFieldChange('funcao', 'OUTROS')
                  } else if (value === 'visitante') {
                    onFieldChange('funcao', 'Visitante')
                  } else if (value === 'voluntario') {
                    onFieldChange('funcao', 'Voluntário')
                  } else if (value === 'equipe') {
                    onFieldChange('funcao', 'Equipe')
                  } else if (value === 'ministrante') {
                    onFieldChange('funcao', 'Ministrante')
                  } else if (value === 'musico') {
                    onFieldChange('funcao', 'Músico')
                  } else if (value === 'lider') {
                    onFieldChange('funcao', 'Líder')
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a função" />
                </SelectTrigger>
                <SelectContent>
                  {modo === 'novo' && <SelectItem value="visitante">Visitante</SelectItem>}
                  <SelectItem value="voluntario">Voluntário</SelectItem>
                  <SelectItem value="equipe">Equipe</SelectItem>
                  <SelectItem value="ministrante">Ministrante</SelectItem>
                  <SelectItem value="musico">Músico</SelectItem>
                  <SelectItem value="lider">Líder</SelectItem>
                  <SelectItem value="outros">Outros...</SelectItem>
                </SelectContent>
              </Select>
              {form.funcao === 'OUTROS' && (
                <Input
                  placeholder="Digite a função..."
                  value=""
                  onChange={(event) => onFieldChange('funcao', event.target.value)}
                  className="mt-2"
                  autoFocus
                />
              )}
              {form.funcao !== '' &&
               form.funcao !== 'Visitante' &&
               form.funcao !== 'Voluntário' &&
               form.funcao !== 'Equipe' &&
               form.funcao !== 'Ministrante' &&
               form.funcao !== 'Músico' &&
               form.funcao !== 'Líder' &&
               form.funcao !== 'OUTROS' && (
                <Input
                  placeholder="Digite a função..."
                  value={form.funcao}
                  onChange={(event) => onFieldChange('funcao', event.target.value)}
                  className="mt-2"
                />
              )}
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              * Informe pelo menos um meio de contato (e-mail ou telefone)
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@exemplo.com"
                  value={form.email}
                  onChange={(event) => onFieldChange('email', event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefone">Telefone</Label>
                <Input
                  id="telefone"
                  placeholder="(00) 00000-0000"
                  value={form.telefone}
                  onChange={(event) => onFieldChange('telefone', event.target.value)}
                />
              </div>
            </div>
          </div>

          <Separator />

          <div className={eventoGratuito ? 'space-y-2' : 'grid gap-4 sm:grid-cols-2'}>
            <div className="space-y-2">
              <Label>Status inicial</Label>
              <Select
                value={form.status}
                onValueChange={(value) => onFieldChange('status', value as StatusInscricao)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmado">Confirmado</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="cancelado">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!eventoGratuito && (
              <div className="space-y-2">
                <Label htmlFor="valor">Valor de inscrição (R$)</Label>
                <Input
                  id="valor"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0,00"
                  value={form.valor}
                  onChange={(event) => onFieldChange('valor', event.target.value)}
                />
              </div>
            )}
          </div>

          {!eventoGratuito && form.valor.trim() !== '' && (
            <div className="space-y-2">
              <Label>Método preferencial de pagamento</Label>
              <Select
                value={form.metodo}
                onValueChange={(value) => onFieldChange('metodo', value as MetodoPagamento)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pix">Pix</SelectItem>
                  <SelectItem value="dinheiro">Dinheiro</SelectItem>
                  <SelectItem value="cartao">Cartão</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="observacoes">Observações</Label>
            <Input
              id="observacoes"
              placeholder="Informações adicionais para a equipe do evento"
              value={form.observacoes}
              onChange={(event) => onFieldChange('observacoes', event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" className="sm:w-auto" onClick={onCancel}>
              Cancelar
            </Button>
            <Button type="submit" className="sm:w-auto" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Registrar participante
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
